import { useMemo } from "react";
// @mui
import { Box, IconButton, Stack, Typography } from "@mui/material";
// hooks
import useResponsive from "~src/hooks/useResponsive";
// components
import Iconify from "~src/components/Iconify";
// utils
import { fDate } from "~src/utils/formatTime";
// types
import { CtaType, TrendingAd, ctaTitle } from "~src/types/ad";
import SaveAdToTrendRocket from "~src/sections/ads/SaveAd/SaveAdToTrendRocket";

enum FacebookAdPublisherPlatform {
  Messenger = "messenger",
  Facebook = "facebook",
  Instagram = "instagram",
  AudienceNetwork = "audience_network",
}

type AdMetaViewProps = {
  ad: TrendingAd;
  focusedCtaUrl?: string | null;
  handleClose?: VoidFunction;
};

//------------------------------------------------------------------------------------------------

export default function AdMetaView({
  ad,
  focusedCtaUrl,
  handleClose,
}: AdMetaViewProps) {
  const isDesktop = useResponsive("up", "lg");
  return (
    <Stack>
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
        }}
      >
        {isDesktop && handleClose && (
          <IconButton onClick={handleClose}>
            <Iconify icon={"material-symbols:close"} width={28} height={28} />
          </IconButton>
        )}
      </Box>
      <SaveAdToTrendRocket
        adId={ad.ad_id}
        sx={{
          mt: 6,
          mb: 2,
          "& .MuiFormControl-root": {
            width: "100%",
          },
          "& .MuiLoadingButton-root": {
            width: "110px",
            mt: "12px",
            ml: "10px",
          },
        }}
      />
      <Typography variant="h6" sx={{
        pb: 1
      }}>
        Ad Details
      </Typography>
      <AdDetailsView ad={ad} focusedCtaUrl={focusedCtaUrl} />
    </Stack>
  );
}

const AdDetailsView = ({
  ad,
  focusedCtaUrl,
}: {
  ad: TrendingAd;
  focusedCtaUrl?: string | null;
}) => {
  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={4}>
        <DetailItem title={"Started running on"} value={fDate(ad.start_date)} />
        <DetailItem title={"ID"} value={ad.id || "-"} />
      </Stack>
      <Stack direction="row" spacing={4}>
        {ad.cta && (
          <DetailItem title={"CTA Type"} value={ctaTitle(ad.cta as CtaType)} />
        )}
        <DetailItem
          title={"Display Type"}
          value={ad.ad_video ? "VIDEO" : "IMAGE"}
        />
        {ad.page_likes !== undefined && (
          <DetailItem
            title={"Page Likes"}
            value={new Intl.NumberFormat("en-AU").format(ad.page_likes)}
          />
        )}
      </Stack>
      <Typography
        sx={{
          color: "rgba(61, 68, 101, 0.4);",
          fontFamily: "Poppins",
          fontSize: "14px"
        }}>
        Platforms
      </Typography>
      <FbAdPublisherPlatformGrayscale
        publisherPlatformString={ad.platform || ""}
      />
      {ad.page_categories && (
        <DetailItem
          title={"Categories"}
          value={parsePageCategories(ad.page_categories)}
        />
      )}
      {focusedCtaUrl && <DetailItem title={"CTA url"} value={focusedCtaUrl} />}
    </Stack>
  );
};

const DetailItem = ({ title, value }: { title: string; value: string }) => {
  return (
    <Stack>
      <Typography
        sx={{
          color: "rgba(61, 68, 101, 0.4);",
          fontFamily: "Poppins",
          fontSize: "14px"
        }}>
        {title}
      </Typography>
      <Typography
        sx={{
          color: "rgba(61, 68, 101, 0.8);",
          fontWeight: 500,
          fontFamily: "Poppins",
          fontSize: "16px"
        }}>
        {value}
      </Typography>
    </Stack>
  );
};

const FbAdPublisherPlatformGrayscale = ({
  publisherPlatformString,
}: {
  publisherPlatformString: string;
}) => {
  const publisherPlatforms = useMemo(() => {
    return parsePublisherPlatform(publisherPlatformString);
  }, [publisherPlatformString]);

  return (
    <Stack direction="row" spacing={1}>
      {publisherPlatforms.map((platform, index) => {
        const icon = publisherPlatformIcon(platform);
        if (icon) {
          return (
            <Box key={index}>
              <Iconify icon={icon} width={20} height={20} color={"grey.500"} />
            </Box>
          );
        }
        return null;
      })}
    </Stack>
  );
};

const publisherPlatformIcon = (
  publisherPlatform: FacebookAdPublisherPlatform,
) => {
  switch (publisherPlatform) {
    case FacebookAdPublisherPlatform.Facebook:
      return "ic:baseline-facebook";
    case FacebookAdPublisherPlatform.Instagram:
      return "iconoir:instagram";
    case FacebookAdPublisherPlatform.Messenger:
      return "ri:messenger-fill";
    case FacebookAdPublisherPlatform.AudienceNetwork:
      return "fluent:globe-24-filled";
    default:
      return null;
  }
};

const parsePublisherPlatform = (publisherPlatformString: string) =>
  publisherPlatformString
    .replace(/'/g, "")
    .replace("[", "")
    .replace("]", "")
    .split(",")
    .map((m) => m.trim()) as FacebookAdPublisherPlatform[];

const parsePageCategories = (publisherPlatforms: string) =>
  publisherPlatforms.replace(/'/g, "").replace("[", "").replace("]", "");
